<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<!--
  @component
  Generates a voronoi layer using [d3-delauney](https://github.com/d3/d3-delauney).
 -->
<script>
  import { getContext } from "svelte";
  import { uniques, raise } from "layercake";
  import { Delaunay } from "d3-delaunay";

  const { data, xGet, yScale, width, height } = getContext("LayerCake");

  let {
    points,
    stroke = undefined,
    activeIndex = $bindable(null),
    onclick = () => {},
    priorityKey = null,
    weightRadius = 18
  } = $props();

  function yGet(/** @type {any} */ d) {
    return $yScale(d.value);
  }
  let pointsXY = $derived(
    points.map((/** @type {any} */ d) => {
      const point = /** @type {any} */ ([$xGet(d), yGet(d)]);
      point.data = d;
      return point;
    })
  );

  let voronoiWidth = $derived(Math.max($width, 1));
  let voronoiHeight = $derived(Math.max($height, 1));

  let delaunay = $derived(Delaunay.from(pointsXY));
  let voronoi = $derived(delaunay.voronoi([0, 0, voronoiWidth, voronoiHeight]));

  let priorityIndices = $derived(
    priorityKey
      ? pointsXY.reduce(
          (/** @type {number[]} */ acc, /** @type {any} */ p, /** @type {number} */ i) => {
            if (p.data.key === priorityKey) acc.push(i);
            return acc;
          },
          []
        )
      : []
  );

  /**
   * Find the nearest point, with priority override.
   * @param {number} px - pointer x in SVG coordinates
   * @param {number} py - pointer y in SVG coordinates
   * @returns {number} index into pointsXY
   */
  function findWeightedNearest(px, py) {
    // Step 1: standard Voronoi lookup — O(sqrt(n))
    const nearestIdx = delaunay.find(px, py);

    // Step 2: if no priority key or nearest is already priority, done
    if (priorityIndices.length === 0 || pointsXY[nearestIdx]?.data.key === priorityKey) {
      return nearestIdx;
    }

    // Step 3: find closest priority point — O(p) where p = priority point count
    let bestIdx = -1;
    let bestDist = Infinity;
    for (const i of priorityIndices) {
      const dx = pointsXY[i][0] - px;
      const dy = pointsXY[i][1] - py;
      const dist = dx * dx + dy * dy; // squared distance (avoid sqrt)
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    // Step 4: if within threshold, prefer priority point
    if (bestIdx >= 0 && bestDist <= weightRadius * weightRadius) {
      return bestIdx;
    }

    return nearestIdx;
  }
</script>

<g aria-hidden="true">
  <rect
    class="voronoi-overlay"
    width={$width}
    height={$height}
    onpointermove={(e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      activeIndex = findWeightedNearest(px, py);
    }}
    onpointerleave={() => {
      activeIndex = null;
    }}
    onclick={(e) => {
      if (activeIndex != null) {
        onclick(points[activeIndex]);
      }
    }}
    onfocus={() => {}}
    onkeydown={() => {}}
    role="presentation"
  />
</g>

<style>
  .voronoi-overlay {
    fill: transparent;
    stroke: none;
    pointer-events: all;
    outline: none;
  }
</style>
